import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRegistration } from '../../hooks/useRegistration';
import { FormErrorBanner } from '../../components/shared/FormErrorBanner';
import { PasswordRequirements } from '../../components/shared/PasswordRequirements';
import { legalDocumentPathForUiLocale, normalizeUiLocale } from '../../legal/documents';

export const RegistrationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    step, setStep,
    trackingNumber, email, name, password, passwordConfirmation,
    loading, fieldErrors, formErrors,
    handleTrackingSubmit, handleRegistrationSubmit, handleChange
  } = useRegistration();

  const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
  const labelClass = "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block";
  const buttonClass = "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 w-full";

  const uiLocale = normalizeUiLocale(i18n?.resolvedLanguage || i18n?.language);
  const policyHref = legalDocumentPathForUiLocale('privacy', uiLocale);
  const linkClass = 'font-medium text-foreground underline underline-offset-2 hover:no-underline';

  const renderFieldError = (errors: string[]) => {
    if (errors.length === 0) {
      return null;
    }

    return <p className="mt-2 text-sm text-destructive">{t(errors[0])}</p>;
  };

  return (
    <div className="max-w-md mx-auto p-6 mt-10">
      <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm">
        <div className="flex flex-col space-y-1.5 p-6">
          <h3 className="text-2xl leading-none tracking-tight">{t('auth.registration.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('auth.registration.subtitle')}</p>
        </div>

        <div className="p-6 pt-0">
          <FormErrorBanner errors={formErrors} className="mb-4" />

          {step === 'tracking' ? (
            <form onSubmit={handleTrackingSubmit} className="space-y-4" noValidate>
              <div>
                <label className={labelClass}>{t('auth.registration.tracking_code_label')}</label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => handleChange({ target: { name: 'trackingNumber', value: e.target.value.toUpperCase() } } as React.ChangeEvent<HTMLInputElement>)}
                  className={inputClass}
                  placeholder={t('auth.registration.tracking_code_placeholder')}
                  required
                  autoFocus
                />
                <p className="text-xs text-muted-foreground mt-1">{t('auth.registration.tracking_code_help')}</p>
                {renderFieldError(fieldErrors.trackingNumber)}
              </div>
              <button type="submit" disabled={loading} className={buttonClass}>
                {loading ? t('auth.registration.validating') : t('auth.registration.validate_button')}
              </button>

              <div className="text-center text-sm mt-4 space-y-1">
                <div>
                  <span className="text-muted-foreground">{t('auth.registration.no_tracking_code')} </span>
                  <a href="/" className="text-foreground underline hover:no-underline font-medium">
                    {t('auth.registration.request_quote')}
                  </a>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('auth.registration.has_account')} </span>
                  <a href="/login" className="text-foreground underline hover:no-underline font-medium">
                    {t('auth.login.submit')}
                  </a>
                </div>
              </div>
            </form>
          ) : (
            <form onSubmit={handleRegistrationSubmit} className="space-y-4" noValidate>
              <div className="bg-muted p-3 rounded border border-border mb-4">
                <p className="text-xs text-muted-foreground uppercase mb-1">{t('auth.registration.tracking_code_label')}</p>
                <p className="font-mono text-sm">{trackingNumber}</p>
              </div>

              <div>
                <label className={labelClass}>{t('auth.registration.email_label')}</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className={`${inputClass} bg-muted text-muted-foreground`}
                />
                <p className="text-xs text-muted-foreground mt-1">{t('auth.registration.email_help')}</p>
                {renderFieldError(fieldErrors.email)}
              </div>

              <div>
                <label className={labelClass}>{t('auth.registration.name_label')}</label>
                <input
                  type="text"
                  name="name"
                  value={name}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
                {renderFieldError(fieldErrors.name)}
              </div>

              <div>
                <label className={labelClass}>{t('auth.registration.password_label')}</label>
                <input
                  type="password"
                  name="password"
                  value={password}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
                <div className="mt-2">
                  <PasswordRequirements password={password} />
                </div>
                {renderFieldError(fieldErrors.password)}
              </div>

              <div>
                <label className={labelClass}>{t('auth.registration.password_confirmation_label')}</label>
                <input
                  type="password"
                  name="passwordConfirmation"
                  value={passwordConfirmation}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
                {renderFieldError(fieldErrors.passwordConfirmation)}
              </div>

              <p className="text-sm text-muted-foreground">
                {t('legal.terms_acceptance.privacy_notice_before')}
                <a href={policyHref} target="_blank" rel="noopener noreferrer" className={linkClass}>
                  {t('legal.privacy')}
                </a>
                {t('legal.terms_acceptance.privacy_notice_after')}
              </p>

              <div className="flex space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep('tracking')}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 w-1/3"
                >
                  {t('auth.registration.back')}
                </button>
                <button type="submit" disabled={loading} className={`${buttonClass} w-2/3`}>
                  {loading ? t('auth.registration.registering') : t('auth.registration.register_button')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
